import {
  CountryCodeSelector,
  getPhoneParts,
  isValidPhoneForSelectedCountry,
} from "@/components/auth/country-code-selector";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useState } from "react";

interface PhoneChangeModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (phoneNumber: string) => Promise<void>;
  currentPhone?: string;
}

export function PhoneChangeModal({
  open,
  onClose,
  onSubmit,
  currentPhone = "",
}: PhoneChangeModalProps) {
  const initialParts = getPhoneParts(currentPhone);
  const [countryCode, setCountryCode] = useState(initialParts.countryCode);
  const [phoneNumber, setPhoneNumber] = useState(initialParts.phoneNumber);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string>("");

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Remove any non-numeric characters except plus sign
    const cleanedValue = e.target.value.replace(/[^0-9]/g, "");
    setPhoneNumber(cleanedValue);
    setError(""); // Clear error when user types
  };

  const handleSubmit = async () => {
    if (!phoneNumber) return;

    const fullPhoneNumber = `${countryCode}${phoneNumber}`;
    if (!isValidPhoneForSelectedCountry(fullPhoneNumber, countryCode)) {
      setError("Please enter a valid phone number");
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit(fullPhoneNumber);
      onClose();
    } catch (error) {
      console.error("Error updating phone number:", error);
      setError("Failed to update phone number. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    const parts = getPhoneParts(currentPhone);
    setCountryCode(parts.countryCode);
    setPhoneNumber(parts.phoneNumber);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Update Phone Number</DialogTitle>
          <DialogDescription>
            Select a country code, then enter the new phone number.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-4 py-4">
          <div className="flex flex-col gap-2">
            <Label htmlFor="phone">Phone Number</Label>
            <div className="flex items-center gap-2">
              <CountryCodeSelector
                value={countryCode}
                onChange={(value) => {
                  setCountryCode(value);
                  setError("");
                }}
                className="h-10 rounded-md border-input bg-background"
              />
              <Input
                id="phone"
                type="tel"
                value={phoneNumber}
                onChange={handlePhoneChange}
                placeholder="Enter your phone number"
                disabled={isSubmitting}
                className={error ? "border-red-500" : ""}
              />
            </div>
            {error && <p className="text-sm text-red-500">{error}</p>}
          </div>
        </div>
        <DialogFooter className="flex flex-col gap-4">
          <p className="text-sm text-yellow-600 dark:text-yellow-500">
            Warning: Updating the user&apos;s phone number will log them out of
            all current sessions.
          </p>
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleSubmit}
              disabled={!phoneNumber || isSubmitting}
            >
              {isSubmitting ? "Updating..." : "Update"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
